/* eslint-disable */
import React, { useEffect, useContext } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  matchPath,
} from "react-router-dom";

// Theme / Global CSS
import { ThemeProvider } from "./theme/ThemeContext";
import GlobalStyle from "./theme/GlobalStyle";

// Context (멤버 구독 컨텍스트)
import { MemberProvider } from "./context/MemberContext";

// Bridge (RN <-> WebView)
import { notifyReady, addAppMessageListener } from "./bridge/appBridge";

// Layouts
import MainLayout from "./layouts/MainLayout";

// Pages
import HomePage from "./pages/HomePage";
import SalesPage from "./pages/SalesPage";
import SalesDetailPage from "./pages/SalesDetailPage";
import ShopCategoriesPage from "./pages/ShopCategoriesPage";
import ShopListPage from "./pages/ShopListPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import ServicesPage from "./pages/ServicesPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import StoreApplyPage from "./pages/StoreApplyPage";

import FranchiseApplyPage from "./pages/FranchiseApplyPage";
import FranchiseApplyFormPage from "./pages/FranchiseApplyFormPage";
import SalesRankingPage from "./pages/SalesRankingPage";
import EduHomePage from "./pages/EduHomePage";
import LocalAffiliatesHome from "./pages/LocalAffiliatesHome";
import MallHomePage from "./pages/MallHomePage";
import NoticePage from "./pages/NoticePage";

import BenefitsPage from "./pages/BenefitsPage";
import SupportPage from "./pages/SupportPage";
import MyInfoPage from "./pages/MyInfoPage";
import UsageHistoryPage from "./pages/UsageHistoryPage";
import FaqPage from "./pages/FaqPage";

// ✅ 포인트(회원) 페이지
import PointsHistoryPage from "./pages/PointsHistoryPage";

// Auth-Kit (기존)
import Callback from "./auth-kit/pages/Callback";
import PhoneMethodRouter from "./auth-kit/pages/PhoneMethodRouter";
import PhonePassPage from "./auth-kit/pages/PhonePassPage";
import PhoneAlimtalkPage from "./auth-kit/pages/PhoneAlimtalkPage";

// Gates
import EnsureMemberGate from "./auth-kit/guards/EnsureMemberGate";
import RequireAnon from "./auth-kit/guards/RequireAnon";
import RouteTrace from "dev/RouteTrace";
import RegisterPage from "pages/RegisterPage";
import SalesMinePage from "pages/SalesMinePage";
import SalesRankingCorpPage from "pages/SalesRankingCorpPage";

// 신규 페이지형 인증 플로우
import LoginPage from "./pages/LoginPage";
import AuthPhonePage from "./pages/AuthPhonePage";
import SignupPage from "./pages/SignupPage";
import LoginLanding from "pages/LoginLanding";
import { GlobalFonts } from "theme/GlobalFonts";
import StoreDetailPage from "pages/StoreDetailPage";
import StorePage from "pages/StorePage";
import TermsPage from "pages/TermsPage";
import PrivacyPage from "pages/PrivacyPage";
import NoticeDetailPage from "pages/NoticeDetailPage";
import EduDetailPage from "pages/EduDetailPage";
import { AppConfigProvider } from "context/AppConfigContext";
import TaxCenterPage from "pages/TaxCenterPage";
import { DriverStatusProvider, useDriverStatusContext } from "context/DriverStatusContext";
import SplashPage from "pages/SplashPage";
import FranchiseStorePage from "pages/FranchiseStorePage";
import FranchiseStoreDetailPage from "pages/FranchiseStoreDetailPage";

// 🔒 이용제한 감시 가드
import MemberBlockGuard from "./auth-kit/guards/MemberBlockGuard";
import NoEmailHarvestPage from "pages/NoEmailHarvestPage";
import NaverBlogPage from "pages/NaverBlogPage";
import StoreOrdersPage from "pages/StoreOrdersPage";
import FranchiseStoreOrdersPage from "pages/FranchiseStoreOrdersPage";
import MyOrdersPage from "pages/MyOrdersPage";
import StoreCartPage from "pages/StoreCartPage";
import StoreCartCheckoutPage from "pages/StoreCartCheckoutPage";
import NotificationsPage from "./pages/NotificationsPage";

import FranchiseCartCheckoutPage from "pages/FranchiseCartCheckoutPage";
import FranchiseStoreCartPage from "pages/FranchiseStoreCartPage";
import { saveFcmTokenForMember } from "services/pushTokenService";
import PartnersPage from "pages/PartnersPage";
import AdvisoryPage from "pages/AdvisoryPage";
import MyAccountPage from "pages/MyAccountPage";

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    const el = document.getElementById("main-scroll");

    if (el) {
      el.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
}

function BridgeBootstrap() {
  useEffect(() => {
    // ✅ WEB_READY 재전송(유실 방지: 0ms/500ms/1500ms)
    try {
      notifyReady();
      setTimeout(notifyReady, 500);
      setTimeout(notifyReady, 1500);
    } catch { }

    const parseMaybe = (raw) => {
      if (typeof raw === "string") {
        try { return JSON.parse(raw); } catch { return null; }
      }
      if (raw && typeof raw === "object") return raw;
      return null;
    };

    const handleRaw = (raw) => {
      const msg = parseMaybe(raw);
      if (!msg || typeof msg !== "object") return;

      const type = msg?.type;
      const payload = msg?.payload || {};

      // ✅ 토큰 수신
      if (type === "FCM_TOKEN") {
        const token = String(payload?.token || "");
        const platform = String(payload?.platform || "");
        if (!token) return;

        const obj = { token, platform, receivedAt: Date.now() };

        // ✅ 1) 런타임 캐시(기존 FcmTokenFlush가 이걸 봄)
        window.__PENDING_FCM_TOKEN__ = obj;

        // ✅ 2) 로컬 저장: 기존 키 + 신규 키 둘 다 저장(불일치 방지/호환)
        try { localStorage.setItem("PENDING_FCM_TOKEN", JSON.stringify(obj)); } catch { }
        try { localStorage.setItem("dhm.pendingFcmToken", JSON.stringify(obj)); } catch { }

        console.log(
          "[WEB][FCM_TOKEN] cached",
          { tail: token.slice(-10), head: token.slice(0, 8), platform }
        );

        return;
      }
    };

    // ✅ RN -> WEB 수신: iOS/Android 케이스 모두 커버
    const onWin = (e) => {
      handleRaw(e?.data);
      if (e?.data?.data) handleRaw(e.data.data);
    };

    const onDoc = (e) => {
      handleRaw(e?.data);
      if (e?.data?.data) handleRaw(e.data.data);
    };

    window.addEventListener("message", onWin);
    document.addEventListener("message", onDoc);

    // ✅ 기존 리스너도 유지
    let off = null;
    try {
      off = addAppMessageListener((msg) => handleRaw(msg));
    } catch { }

    return () => {
      try { window.removeEventListener("message", onWin); } catch { }
      try { document.removeEventListener("message", onDoc); } catch { }
      try { off && off(); } catch { }
    };
  }, []);

  return null;
}

function FcmTokenFlush() {
  const ds = useDriverStatusContext();

  const phoneE164 =
    ds?.phoneE164 ||
    ds?.user?.phoneE164 ||
    ds?.member?.phoneE164 ||
    "";

  useEffect(() => {
    const pending = window.__PENDING_FCM_TOKEN__;
    if (!phoneE164) return;
    if (!pending?.token) return;

    const { token, platform } = pending;

    (async () => {
      try {
        await saveFcmTokenForMember({ phoneE164, token, platform });
        window.__PENDING_FCM_TOKEN__ = null;
      } catch (e) {
      }
    })();
  }, [phoneE164]);

  return null;
}

// ---- 라우팅 래퍼: 메인 레이아웃 (헤더+풋바) 적용 구간 ----
function AppRoutes() {
  const location = useLocation();

  const TAB_ROOTS = new Set([
    "/home",
    "/sales",
    "/shop/categories",
    "/services/categories",
    "/my",
    "/benefits",
    "/support",
    "/faq",
    "/notifications",
  ]);

  const AUTH_OR_SOLO = new Set([
    "/loginlanding",
    "/auth",
    "/splash",
    "/login",
    "/auth/phone",
    "/auth/phone/pass",
    "/auth/phone/alimtalk",
    "/auth/callback/naver",
    "/auth/callback/kakao",
    "/auth/callback/google",
    "/auth/signup",
    "/store/apply",
    "/franchise/apply",
    "/sales/ranking",
    "/edu",
    "/local/affiliates",
    "/tax-center",
    "/mall",
    "/notice",
    "/history",
    "/register",
    "/sales/mine",
    "/sales/ranking/corp",
    "/no-email-harvest",
    "/naver-blog",
    "/orders",
    "/partners", // ✅ 추가
    "/advisory",
    "/my/account",
  ]);

  const SOLO_GLOBS = [
    "/notice/:id",
    "/edu/:id",
    "/franchise/apply/:type",
  ];

  const path = useLocation().pathname;

  const isSolo =
    AUTH_OR_SOLO.has(path) ||
    SOLO_GLOBS.some((p) => matchPath({ path: p, end: true }, path));

  if (isSolo) {
    return (
      <>
        <GlobalFonts />
        <Routes>
          <Route
            path="/loginlanding"
            element={
              <RequireAnon>
                <LoginLanding />
              </RequireAnon>
            }
          />

          <Route
            path="/splash"
            element={
              <RequireAnon>
                <SplashPage />
              </RequireAnon>
            }
          />

          <Route
            path="/auth"
            element={
              <RequireAnon>
                <LoginLanding />
              </RequireAnon>
            }
          />

          <Route
            path="/login"
            element={
              <RequireAnon>
                <LoginPage />
              </RequireAnon>
            }
          />

          <Route
            path="/auth/phone"
            element={
              <RequireAnon>
                <AuthPhonePage />
              </RequireAnon>
            }
          />

          <Route path="/auth/phone/pass" element={<PhonePassPage />} />
          <Route path="/auth/phone/alimtalk" element={<PhoneAlimtalkPage />} />

          <Route
            path="/auth/signup"
            element={
              <RequireAnon>
                <SignupPage />
              </RequireAnon>
            }
          />

          <Route path="/auth/callback/:provider" element={<Callback />} />

          {/* 프랜차이즈 가입 선택 화면 */}
          <Route path="/franchise/apply" element={<FranchiseApplyPage />} />
          {/* 프랜차이즈 가입 폼 (개인/법인) */}
          <Route
            path="/franchise/apply/:type"
            element={<FranchiseApplyFormPage />}
          />

          <Route path="/sales/ranking" element={<SalesRankingPage />} />
          <Route path="/sales/mine" element={<SalesMinePage />} />
          <Route path="/sales/ranking/corp" element={<SalesRankingCorpPage />} />
          <Route path="/edu" element={<EduHomePage />} />
          <Route path="/edu/:id" element={<EduDetailPage />} />
          <Route path="/local/affiliates" element={<LocalAffiliatesHome />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/advisory" element={<AdvisoryPage />} />
          <Route path="/mall" element={<MallHomePage />} />
          <Route path="/notice" element={<NoticePage />} />
          <Route path="/notice/:id" element={<NoticeDetailPage />} />
          <Route path="/store/apply" element={<StoreApplyPage />} />

          <Route path="/orders" element={<MyOrdersPage />} />

          <Route path="/history" element={<UsageHistoryPage />} />
          <Route path="/tax-center" element={<TaxCenterPage />} />
          <Route path="/no-email-harvest" element={<NoEmailHarvestPage />} />
          <Route path="/naver-blog" element={<NaverBlogPage />} />
          <Route path="/my/account" element={<MyAccountPage />} />

          <Route path="*" element={<Navigate to="/splash" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <GlobalFonts />
      <MainLayout showBottomNav={TAB_ROOTS.has(path)}>
        <Routes>
          <Route path="/home" element={<HomePage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/sales" element={<SalesPage />} />
          <Route path="/sales/detail" element={<SalesDetailPage />} />
          <Route path="/shop/categories" element={<ShopCategoriesPage />} />
          <Route path="/shop/list" element={<ShopListPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/services/categories" element={<ServicesPage />} />
          <Route path="/services/:id" element={<ServiceDetailPage />} />
          <Route path="/benefits" element={<BenefitsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/store/orders" element={<StoreOrdersPage />} />
          <Route path="/store/cart/checkout" element={<StoreCartCheckoutPage />} />
          <Route path="/store/cart" element={<StoreCartPage />} />
          <Route path="/store/:id" element={<StoreDetailPage />} />
          <Route path="/franchise-store" element={<FranchiseStorePage />} />
          <Route path="/franchise/orders" element={<FranchiseStoreOrdersPage />} />
          <Route path="/franchise-store/:id" element={<FranchiseStoreDetailPage />} />
          <Route path="/franchise-store/cart" element={<FranchiseStoreCartPage />} />
          <Route path="/franchise-store/cart/checkout" element={<FranchiseCartCheckoutPage />} />

          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          <Route path="/my" element={<MyInfoPage />} />

          {/* ✅ 내 포인트 사용 내역 */}
          <Route path="/points/history" element={<PointsHistoryPage />} />
        </Routes>
      </MainLayout>
    </>
  );
}

function BootRedirect() {
  return <Navigate to="/splash" replace />;
}

function AppInner() {
  useEffect(() => {
    notifyReady();
    const unsub = addAppMessageListener();
    return () => {
      unsub && unsub();
    };
  }, []);

  return (
    <>
      <ScrollToTop />
      <MainLayout />
    </>
  );
}

export default function App() {
  return (
    <AppConfigProvider>
      <ThemeProvider>
        <MemberProvider>
          <DriverStatusProvider>
            <BrowserRouter>
              {process.env.NODE_ENV !== "production" && <RouteTrace />}

              <MemberBlockGuard />

              <BridgeBootstrap />
              <FcmTokenFlush />
              <ScrollToTop />

              <Routes>
                <Route path="/" element={<BootRedirect />} />
                <Route path="/*" element={<AppRoutes />} />
              </Routes>

              <GlobalStyle />
            </BrowserRouter>
          </DriverStatusProvider>
        </MemberProvider>
      </ThemeProvider>
    </AppConfigProvider>
  );
}
